// FixedSizeVirtualizedListbox.jsx
// Simple virtualization: listbox receives pre-rendered option elements as children,
// virtualizes with react-window FixedSizeList. Row renders children[index] in a positioned div.
// ListboxProps: { direction, itemHeight?, listHeight? } for RTL and size.
// On open, scrolls to the selected option (aria-selected) so it is in view.

import React from 'react';
import { FixedSizeList as List } from 'react-window';

const DEFAULT_ITEM_HEIGHT = 50;
const DEFAULT_LIST_HEIGHT = 320;

function hasAriaSelected(element) {
    const ariaSelected = element?.props?.['aria-selected'];
    if (ariaSelected === true || ariaSelected === 'true') return true;
    const firstChild = React.Children.toArray(element?.props?.children)?.[0];
    return firstChild?.props?.['aria-selected'] === true || firstChild?.props?.['aria-selected'] === 'true';
}

function findSelectedIndex(itemData) {
    for (let i = 0; i < itemData.length; i++) {
        if (hasAriaSelected(itemData[i])) return i;
    }
    return -1;
}

const FixedSizeVirtualizedListbox = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, direction, itemHeight = DEFAULT_ITEM_HEIGHT, listHeight = DEFAULT_LIST_HEIGHT, ...other } = props;
    const itemData = React.useMemo(() => React.Children.toArray(children), [children]);
    const itemCount = itemData.length;
    const dir = direction === 'rtl' ? 'rtl' : 'ltr';
    const isRtl = dir === 'rtl';

    // Fit short lists to content so we don't force a tall scrollable viewport
    // (avoids nested scrollbars when parent also has max-height).
    const height = Math.min(
        listHeight,
        Math.max(itemCount * itemHeight, itemHeight || DEFAULT_ITEM_HEIGHT)
    );

    const initialScrollOffset = React.useMemo(() => {
        const selectedIndex = findSelectedIndex(itemData);
        if (selectedIndex <= 0) return 0;
        const offset = selectedIndex * itemHeight;
        const maxOffset = Math.max(0, itemCount * itemHeight - height);
        return Math.min(offset, maxOffset);
    }, [itemData, itemHeight, itemCount, height]);

    return (
        <div
            ref={ref}
            dir={dir}
            {...other}
            style={{
                ...(other.style || {}),
                direction: dir,
                padding: 0,
                margin: 0,
                overflow: 'hidden',
                maxHeight: 'none',
            }}
        >
            <List
                height={height}
                itemCount={itemCount}
                itemSize={itemHeight}
                width="100%"
                direction={dir}
                itemData={itemData}
                initialScrollOffset={initialScrollOffset}
                style={{ overflowX: 'hidden' }}
            >
                {({ index, style, data }) => (
                    <div
                        style={{
                            ...style,
                            width: '100%',
                            boxSizing: 'border-box',
                            textAlign: isRtl ? 'right' : 'left',
                            direction: dir,
                        }}
                    >
                        {data[index]}
                    </div>
                )}
            </List>
        </div>
    );
});

export default FixedSizeVirtualizedListbox;
